import { useEffect, useState } from 'react'
import {
  chooseNativeApplication, nativeAccessibilityStatus, nativeApplicationsSupported,
  setNativeApplicationAccess, type HandsAuthority,
} from '@/services/desktopRuntime'

export function NativeApplicationAccess({ authority, onChange }: {
  authority: HandsAuthority
  onChange: (value: HandsAuthority) => void
}) {
  const [supported, setSupported] = useState<boolean | null>(null)
  const [checkAttempt, setCheckAttempt] = useState(0)
  const [access, setAccess] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    void nativeApplicationsSupported().then(async (value) => {
      if (!alive) return
      setSupported(value)
      if (value) {
        const granted = await nativeAccessibilityStatus()
        if (alive) setAccess(granted)
      }
    }).catch(() => { if (alive) setError('App access could not be checked. Try again.') })
    return () => { alive = false }
  }, [checkAttempt])
  if (supported === false) return null
  if (supported === null) return (
    <section aria-label="Connected applications" className="p-5 text-[15px] text-[var(--text-secondary)]">
      {error ? <><p role="alert">{error}</p><button type="button" className="mt-2 font-semibold" onClick={() => {
        setError(null)
        setCheckAttempt((value) => value + 1)
      }}>Retry app access</button></> : <p>Checking app access…</p>}
    </section>
  )
  const applications = authority.resourceGrants.filter((grant) => grant.resourceType === 'native_application')
  const update = async (action: () => Promise<HandsAuthority>) => {
    setBusy(true)
    setError(null)
    try { onChange(await action()) } catch { setError('App access could not be confirmed. Check the connection before retrying.') }
    finally { setBusy(false) }
  }
  const button = 'rounded-lg border border-[var(--border)] px-3 py-2 font-semibold hover:bg-[var(--bg-hover)] disabled:opacity-60'
  return (
    <section aria-label="Connected applications" className="rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-5 text-[15px] text-[var(--text-primary)]">
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-semibold">Applications on this Mac</h2>
        <button type="button" className={button} disabled={busy} onClick={() => void update(chooseNativeApplication)}>
          {busy ? 'Updating…' : 'Choose app'}
        </button>
      </div>
      {!applications.length && <p className="mt-2 text-[var(--text-secondary)]">Choose an app, then ask Ricald to work in it. New connections start read-only.</p>}
      {access !== true && (
        <div className="mt-3 text-[var(--text-secondary)]">
          <p>{access === null ? 'Checking macOS Accessibility access…' : 'Allow Ricald Desktop in macOS Accessibility settings to use connected apps.'}</p>
          <button type="button" className="mt-2 font-semibold text-[var(--accent)]" disabled={busy} onClick={() => {
            setBusy(true)
            void nativeAccessibilityStatus().then(setAccess).catch(() => setError('Access could not be checked. Try again.')).finally(() => setBusy(false))
          }}>Check access</button>
        </div>
      )}
      <ul className="mt-3 space-y-4">
        {applications.map((grant) => (
          <li key={grant.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-[var(--border)] pt-3">
            <span className="font-medium">{grant.label}</span>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={grant.edit === true} disabled={busy} onChange={(event) => void update(() => setNativeApplicationAccess(grant.id, event.target.checked, grant.press === true))} />
              Allow typing
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={grant.press === true} disabled={busy} onChange={(event) => void update(() => setNativeApplicationAccess(grant.id, grant.edit === true, event.target.checked))} />
              Use controls after approval
            </label>
            <button type="button" className="ml-auto font-medium text-[var(--text-secondary)] hover:text-[var(--state-danger)]" disabled={busy} aria-label={`Disconnect ${grant.label}`} onClick={() => void update(() => setNativeApplicationAccess(grant.id, false, false, true))}>Disconnect</button>
          </li>
        ))}
      </ul>
      {error && <p role="alert" className="mt-3 text-[var(--state-danger)]">{error}</p>}
    </section>
  )
}
